import React, { useEffect, useState } from 'react';
import { Route, Router, Switch, useHistory } from 'react-router-dom';
import { Spin, notification } from 'antd';
import ReactTracker from 'react-tracker-teko';
import { commonConstants } from 'constants/index';
import { browserHistory } from 'helpers';
import { t } from 'helpers/i18n';
import TekoID from 'teko-oauth2';
import Page403 from 'containers/shared/Page403';
import Page404 from 'containers/shared/Page404';
import Page500 from 'containers/shared/Page500';
import PrivateRoute from 'components/shared/PrivateRoute';
import './App.scss';
import { Provider, useDispatch } from 'react-redux';
import store from './store/configureStore';
import { observeStore } from 'store/util';
import { RootState } from 'store/reducer';
import { PopupDescription } from 'store/popup/util';
import PopupHelper from 'components/PopupHelper';
import ProductAppLayout from 'containers/Product/ProductAppLayout';
import Home from 'containers/Home';
import { OnGoingGuide } from 'data/ui/guide/Guide';
import { UndefinedAble } from 'data/base';
import AppTour from 'AppTour';
import { markGUIReady } from 'containers/Guide/store/guide/actions';
import { SELECTOR_MATCH_ALL } from 'containers/Guide/store/guide/reducer';
import SposErrorTracer from 'containers/Tool/SposErrorTracer';
import { fetchPlatforms } from 'containers/store/common/actions/FetchPlatforms';
import { fetchMarkets } from 'containers/store/common/actions/FetchMarkets';
import { fetchEnvironments } from 'containers/store/common/actions/FetchEnvironments';
import { fetchProducts } from 'containers/store/productSelector/actions/FetchProducts';
import { fetchUserRoles } from 'containers/store/common/actions/FetchUserRoles';
import { fetchModules } from 'containers/store/common/actions/FetchModules';
import Terra from 'services/apis/terra/Terra';
import { fetchServices } from 'containers/store/common/actions/FetchServices';
import PlaygroundContainer from 'containers/PlaygroundContainer';
import ProjectDetail from 'v2/containers/ProjectDetail/ProjectDetail';
import ProjectList from 'v2/containers/ProjectList/ProjectList';
import { getV2HomeUrl, Routes } from 'constants/routes';
import AppLoading from 'AppLoading';
import CreateProject from './v2/containers/CreateProject/CreateProject';
import PrivateRouteWithOrganization from 'components/shared/PrivateRoute/PrivateRouteWithOrganization';
import {
  getAuthConfig,
  saveAuthConfigFromQueryParamsAndRemoveAuthConfigQueryParamsIfAny,
} from 'helpers/auth';
import SilenceLoginUncompleted from 'v2/components/auth/SilenceLoginUncompleted';
import { NotificationDescription } from 'store/notification/util';
import NotificationHelper from 'components/NotificationHelper';

const { IAM_SCOPES } = commonConstants;

const reactTracker = new ReactTracker({
  // Configure your tracker server and site by providing
  host:
    (window.REACT_APP_TRACKER_APP_ID && window.REACT_APP_TRACKER_HOST) || '',
  urlServeJsFile:
    (window.REACT_APP_TRACKER_APP_ID && window.REACT_APP_TRACKER_JS_FILE) || '',
  appId: window.REACT_APP_TRACKER_APP_ID || '',
});

const AppWithRouter: React.FC = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [notificationInstance, notificationContextHolder] = notification.useNotification();
  NotificationHelper.setNotification(notificationInstance);
  useEffect(() => {
    // Init common data
    dispatch(fetchProducts());
    dispatch(fetchPlatforms());
    dispatch(fetchMarkets());
    dispatch(fetchEnvironments());
    dispatch(fetchUserRoles());
    dispatch(fetchServices());
    dispatch(fetchModules());

    // Subscribe popup event
    const unsubscribePopup = observeStore<RootState, PopupDescription>(
      store,
      (rootState: RootState) => {
        return rootState.popup.popupDescription;
      },
      (selectedState: PopupDescription) => {
        PopupHelper.showPopup(selectedState);
      }
    );

    const unsubscribeNotification = observeStore<RootState, NotificationDescription>(
      store,
      (rootState: RootState) => {
        return rootState.notification.notificationDescription;
      },
      (selectedState: NotificationDescription) => {
        NotificationHelper.showNotification(selectedState);
      }
    )

    // Subscribe popup event
    const unsubscribeGuide = observeStore<
      RootState,
      UndefinedAble<OnGoingGuide>
    >(
      store,
      (rootState: RootState) => {
        return rootState.guide.onGoingGuide;
      },
      (guideState: UndefinedAble<OnGoingGuide>) => {
        if (guideState) {
          const currentLocation = document.location.pathname;
          const targetLocation =
            guideState.steps[guideState.currentStep].location;

          if (currentLocation !== targetLocation) {
            history.push(targetLocation);
          } else if (!guideState.isGUIReady) {
            dispatch(markGUIReady(SELECTOR_MATCH_ALL));
          }
        }
      }
    );

    return () => {
      // code will be called on unmount
      unsubscribePopup();
      unsubscribeGuide();
      unsubscribeNotification();
    };
  }, [history, dispatch]);

  return (
    <>
      {notificationContextHolder}
      <Switch>
        <Route exact path={Routes.page403} component={Page403} />
        <Route exact path={Routes.page404} component={Page404} />
        <Route exact path={Routes.page500} component={Page500} />
        <Route exact path={Routes.playground} component={PlaygroundContainer} />
        <PrivateRoute
          path="/products/:productId"
          name={t('Products')}
          component={ProductAppLayout}
        />
        <PrivateRoute
          path="/tools/spos-error-tracer/:traceCode"
          name={t('SposErrorTracer')}
          component={SposErrorTracer}
        />
        <PrivateRouteWithOrganization
          path={Routes.v2_newProject}
          name={t('V2')}
          component={CreateProject}
        />
        <PrivateRouteWithOrganization
          path={Routes.v2_projectDetail}
          name={t('V2')}
          component={ProjectDetail}
        />
        <PrivateRouteWithOrganization
          path={Routes.v2_projectList}
          name={t('V2')}
          component={ProjectList}
        />
        <PrivateRoute path="/" name={t('Home')} component={Home} />
      </Switch>
    </>
  );
};

const App: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [silenceLoginInCompleted, setSilenceLoginInCompleted] = useState<
    boolean
  >();
  const history = reactTracker.connectToHistory(browserHistory);

  useEffect(() => {
    saveAuthConfigFromQueryParamsAndRemoveAuthConfigQueryParamsIfAny();
    const authConfig = getAuthConfig();
    if (!authConfig) {
      // Because some pages do not require auth, in the case of no auth config,
      // we still finish initialization process and go to the expected page of user.
      // Then the private screens (routes) will redirect the user to the login page.
      setLoading(false);
      return;
    }

    // init TEKOID
    TekoID.init({
      clientId: authConfig.clientId,
      redirectUri: getV2HomeUrl(),
      oauthDomain: authConfig.authDomain,
      scopes: IAM_SCOPES,
      monitorSession: true,
      checkTokenRevoked: true,
      silent: true,
    }).then(async () => {
      if (TekoID.user.isLoggedIn()) {
        upsertUser();
      } else {
        setSilenceLoginInCompleted(true);
      }

      setLoading(false);
    });
  }, []);

  const upsertUser = async () => {
    const user = TekoID.user.getUserInfo();
    const result = await Terra.user.upsertUser({
      sub: user.sub,
      email: user.email,
      name: user.name,
      picture: user.picture,
      phone: user.phone_number,
    });
    if (!result.isSuccess()) {
      console.log(result.exception().message);
    }
  };

  if (silenceLoginInCompleted) {
    return <SilenceLoginUncompleted />;
  }

  if (loading) {
    return <Spin className="app-spin" />;
  }

  return (
    <Provider store={store}>
      <Router history={history}>
        <AppWithRouter />
      </Router>
      <AppTour />
      <AppLoading />
    </Provider>
  );
};

export default App;
